import { IAddItem, IMutateAllocationBagValue, ISizeRange } from "actions/collections";
import { ColumnApi, GridApi, GridReadyEvent, ICellRendererParams, IDetailCellRendererParams } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";

import { ICurrency } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";

import {
    IBuiltDoorTotals,
    SMU_FILTER,
    ToolPanels,
    UnitsEntryType,
    ValidationModes,
    View,
} from "constants/product-collection";
import { IImageCellDimensions } from "constants/types";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { RollupType } from "containers/folders/types/server-rollups";

import { AllocationBag, IAllocationDimension, IAllocationDimensionValue } from "helpers/allocation-dimension";
import { IGridParamsQueryParam, IPreference, ISaveStateToGridQueryParamsActions } from "./tool-panels";
import { ISuggestionItem, ISuggestionObject } from "types/product-collection-suggestions";
import { IGridData } from "helpers/collection";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { IDoor } from "helpers/door-collection";
import { FeatureFlags } from "helpers/feature-flags";
import { MetricsKind } from "helpers/metrics/types";

import { IDisplayFields, ISchemaKey, DistributionTargets } from "helpers/product-collection";

import { List, Map, OrderedSet, Set } from "immutable";
import set from "lodash/set";
import * as React from "react";
import { IDoorTotalRow, IDoorTotals, IPackInfo } from "reducers/collection";
import "styles/ag-grid/sizingGrid.styl";

import { IProductCollectionTileGridContext } from "./grid-renderers/full-width-cell-renderer";

export interface IProps {
    shopViewFields?: IDisplayFields;
    allocationDimensions: IAllocationDimension[];
    allocationDimensionGroups: IAllocationDimension[];
    aggFunc: any;
    allowedValueFields: any;
    brandEntities: any;
    budgets: any;
    bulkAddingItems: boolean;
    bulkItems: any;
    collection?: Map<string, any>;
    capabilities: nuorder.folder.v1.Capabilities;
    collectionBudgetActuals: any;
    collectionId?: string; // indicates if this is used in assortments context or legacy cart.
    copiedAllocation: Map<string, any> | null;
    copiedAllocationName: string;
    copiedAllocationSizes: string;
    copiedDoorAllocationName?: string;
    copyAllocation: (
        copiedAllocation: Map<string, any> | null,
        copiedAllocationName: string,
        copiedAllocationSizes: string,
        copiedBulks?: Map<string, any> | null,
    ) => void;
    copyItemsString: string;
    currentViewName: string;
    currentAllocationDimensionView: IPreference[];
    customFilteredItems?: Set<string>;
    // doors collection related
    dates: IAllocationDimensionValue[];
    doors: IDoor[];
    doorClassificationSchema: ISchemaKey[];
    dimensionsPanelApplied: DimensionsPanelReduxState;
    dataEntryType?: UnitsEntryType;
    editable: boolean;
    filter: any;
    filterAssortmentByBrand?: string;
    filterAssortmentBySMU?: string;
    focusOnCellMap: Map<string, any>;
    getSuggestionData: () => ISuggestionObject;
    gridParams: IGridParamsQueryParam;
    hashtags: Set<string>;
    isDraggable: boolean;
    isResetColumns: boolean;
    isResizing?: boolean;
    itemDisplayProperty?: List<any>;
    items?: List<Map<string, any>>;
    itemsId?: string;
    location: any;
    onAddItem: (params: IAddItem) => void;
    onBulkItemsSet?: (ids: OrderedSet<string>) => void;
    onChangeCurrentView: (currentView: List<Map<string, any>>) => void;
    onColumnGroupOpened: (groupId: string, isExpanded: boolean) => void;
    onFilterAssortmentBySMU: (value: SMU_FILTER) => void;
    onFilterChanged?: (gridData: IGridData) => void;
    onFirstDataRendered?: (gridData: IGridData) => void;
    onFocusChange: (collectionId: string, itemsId: string, field: string) => void;
    onFetchCollectionPOI: (poi: string) => void;
    onGridMount?: (api: GridApi, columnApi: ColumnApi, gridRef: React.Ref<AgGridReact>) => void;
    onFocusOnCellMap?: () => Map<string, any>;
    onGridParamsChange: (params: ISaveStateToGridQueryParamsActions, search?: string) => void;
    onViewParamsChange: (
        gridParams: IGridParamsQueryParam,
        updatedDimensionsState: DimensionsPanelReduxState,
        search: string,
        shouldLoadNewSavedView?: boolean,
    ) => void;
    onClearViewParams: () => void;
    onLinkProductToItem: (rowId: string) => void;
    onChangeDefaultItemVisibility?: (e?: MouseEvent) => void;
    onMoveItem: (itemsId: string, index: number, destinationIndex: number, rowId: string) => void;
    onMutateCollectionBudget: (id: string, params: any) => void;
    onMutateReviewPlan: (dimensionPath: string[], totalType: any, value: string | number) => void;
    onMutateMetricsReviewPlan: (dimensionPath: string[], metricsKind: MetricsKind, value: string | number) => void;
    onMutateCollectionItem: (itemsId, index, item, value?, meta?, fieldName?) => void;
    onMutateDefaultItem: (item: Map<string, any>, value?: any) => void;
    onClearAllocations: (itemId: string) => void;
    onOpenDeleteConfirmation: (itemsid, itemIndex) => void;
    onOpenProductModal: (id: string, assortmentId?: string, nodeRowIndex?: number) => void;
    onPasteItems: () => void;
    onResetColumns: (reset) => void;
    onRowDataSet: () => void;
    onPasteDoorAllocations: (itemsId: string, door: string, filterAssortmentByBrand: string) => void;
    onSearchChange: (searchText) => void;
    onSetTileViewData: (filteredGridData: IGridData, allGridData: IGridData) => void;
    onSnackbarAdd: (params) => void;
    onSubscribeToItemsUpdates: () => void;
    onToolPanelVisibleChanged: ({ api }: { api: GridApi }) => void;
    onUnsubscribeToItemsUpdates: () => void;
    onUpdateCollectionAttribute: (id, prop, value) => void;
    handleOnChangeDoorGroupingLabel: (event: React.MouseEvent, doorGrouping: string) => void;
    pasteAllocation: (rowData: any, rowIndex: number) => void;
    pivot: any;
    router: any;
    rowGroup: any;
    scrollToIndex?: number;
    searchText: string;
    setCopyItemsString: (newValue: string) => void;
    setSuggestionData: (data: ISuggestionItem[], storeKey: string) => void;
    sizePacks: { [key: string]: { [key1: string]: IPackInfo } };
    showTotals: boolean;
    sort: any;
    view: View;
    allocationEnabled: boolean;
    sizingAllocationEnabled: boolean;
    sizePackEnabled: boolean;
    detailFields: IDisplayFields;
    toolpanelOpened: ToolPanels | null;
    sizeRanges: ISizeRange[];
    onResetSizeRanges: () => void;
    onLookupAssortmentErrors: (assortmentsData?: Array<{ [key: string]: string }>) => void;
    dimensionPlanMetrics: string[];
    accessPlan: boolean;
    isRollupMode?: boolean;
    rollupType?: RollupType;
    onExportToExcel: () => void;
    syncingReviewPlans: boolean;
    reviewPlanRows: object[];
    reviewPlan: any;
    onFetchPinnedRows: (showTotals: boolean, dimensionPlanMetrics: string[], accessPlan: boolean) => void;
    isAllocationDimensionModalOpen: boolean;
    recreateServerRollupSnapshot?: boolean;
    doorTotals: IBuiltDoorTotals | {};
    doorTotalRowFiltered?: IDoorTotalRow;
    merchantId: string;
    legacyMerchantId: string;
    sizeTableType?: string;
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    allowDuplicateProduct: boolean;
    merchantCurrency?: ICurrency;
    onPinnedRowOrder: (columnOrder: any) => void;
    pinnedRowColumnOrder: any;
    poiToItemsToSizeBulksMap?: Map<string, Map<string, number>>;
    defaultDateFormat?: string;
    defaultItem?: any;
    showDefaultItem: boolean;
    canShopBrand?: boolean;
    onSetDoorTotals: (doorTotals: IDoorTotals) => void;
    onSetFilteredDoorTotals: (doorTotals: IDoorTotals) => void;
    onSetDoorTotalRow?: (doorTotals: IDoorTotalRow) => void;
    tileGridContext?: IProductCollectionTileGridContext;
    onSetImageCellDimensions: (dimensions: IImageCellDimensions) => void;
    imageCellDimensions: IImageCellDimensions;
    activeValidationMode?: ValidationModes;
    itemsToBeValidated?: Set<any>;
    errorLog?: Map<string, any>;
    isServerRollup?: boolean;
    expandAllGroups: boolean;
    flags?: FeatureFlags;
    onEditDoorGroupingRequest: () => void;
    onImportDoorAttributes: () => void;
    onMutateBagValues: (newBagValue: IMutateAllocationBagValue) => void;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    inventoryEnabled?: boolean;
    shipmentReviewDetailRenderer?: (params: ICellRendererParams) => Partial<IDetailCellRendererParams>;
    scrollToDoorsColumnOnLoad: boolean;
    setScrollToDoorsColumnOnLoad: (newValue: boolean) => void;
    startingMutationCount?: number;
    onGridReady?: (e: GridReadyEvent) => void;
    itemsRevision?: number;
    fetchSavedViewData?: (viewData: string[]) => void;
    showTooManyOperationsDialog: () => void;
    onStartCreateRollupSnapshot?: () => void;
    onFetchCollectionDriveItem?: (merchantId: string, rollupId: string, collectionIds: string[]) => void;
    collectionDriveItem?: nuorder.folder.v1.Item;
    merchantAllocationDimensions?: IAllocationDimension[];
    getAlignedGrids?: () => AgGridReact[] | undefined;
    switchingTile?: boolean;
    handleSwitchingTileAndGrid?: (switching: boolean) => void;
    metricsEnabled?: boolean;
    distributionTargets: DistributionTargets;
    isCollapseModeOn: boolean;
    selectedTargetIds: Set<string>;
    collapseMetric?: MetricsKind;
}

export interface IGridContext {
    dimensionFilters: string[][];
    doorIdsWithinClasifications: string[];
    doorTotals: IBuiltDoorTotals | {};
    doors: IDoor[];
    doorTotalRowFiltered: IDoorTotalRow;
    hasDeliveries: boolean;
    poiToItemsToSizeBulksMap?: Map<string, Map<string, number>>;
    onFetchCollectionPOI: (poi: string) => void;
    sizePacks: { [key: string]: { [key1: string]: IPackInfo } };
    onUpdateNote: (itemId: string, colId: string, value: string, node) => void;
    getNoteValue: (itemId: string, colId: string, collectionId?: string) => void;
    getRequestedItemId: () => string;
    getRequestedColumnId: () => string;
    clearRequestedItemId: () => void;
    editable: boolean;
    isServerRollup?: boolean;
    tileGridContext?: IProductCollectionTileGridContext;
    activeValidationMode?: ValidationModes;
    itemsToBeValidated?: Set<any>;
    exportStatuses: Map<string, Map<string, string>>;
    onChangeErrorLogLocation?: (params: any) => void;
    dataEntryType?: UnitsEntryType;
    onMutateBagValues: (newBagValue: IMutateAllocationBagValue) => void;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    getFocusOnCellMap?: () => Map<string, any>;
    distributionTargets: DistributionTargets;
    isDistributionEnabled: boolean;
}

/**
 * Returns an object containing the value type and the current value
 */
const getTypeAndValue = (x) => {
    const valueType = typeof x;
    let t = "string_value";
    let v;
    switch (valueType) {
        case "boolean":
            t = "bool_value";
            v = x;
            break;
        case "string":
            t = "string_value";
            v = (x || "").toString();
            break;
        case "number":
            // NOTE: Number needs to transform to string
            // or will have issue: json: cannot unmarshal number into Go value of type string
            t = "string_value";
            v = x.toString();
            break;
    }
    return { t, v };
};

export const defaultValueSetter = ({ colDef, newValue, data }) => {
    const typeAndValue = getTypeAndValue(newValue);
    set(data, ["fields", colDef.field, typeAndValue.t], typeAndValue.v);
    return true;
};

export const dateValueSetter = ({ colDef, newValue, data }) => {
    set(data, ["fields", colDef.field, "date_value"], newValue);
    return true;
};

export const checkType = (value) => {
    return typeof value === "boolean" ? "bool_value" : "string_value";
};
